import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, setDoc, deleteDoc, onSnapshot, query, where } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

export interface PresenceUser {
  uid: string;
  email: string;
  name: string;
  initials: string;
  color: string;
  lastSeen: number;
}

const AVATAR_COLORS = [
  'bg-emerald-500', 'bg-indigo-500', 'bg-purple-500', 'bg-rose-500', 
  'bg-amber-500', 'bg-teal-500', 'bg-cyan-500', 'bg-sky-500'
];

function getHashColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

function getInitials(nameOrEmail: string): string {
  if (!nameOrEmail) return 'U';
  const namePart = nameOrEmail.split('@')[0];
  const parts = namePart.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return namePart.substring(0, 2).toUpperCase();
}

export function useProjectPresence(projectId: string | undefined | null) {
  const { user } = useAuth();
  const [activeUsers, setActiveUsers] = useState<PresenceUser[]>([]);

  useEffect(() => {
    if (!projectId || !user || user.uid === 'e2e_local_uid') {
      setActiveUsers([]);
      return;
    }

    const presenceDocId = `${projectId}_${user.uid}`;
    const presenceRef = doc(db, 'projectPresence', presenceDocId);
    const userEmail = user.email || 'user@domain.com';
    const userName = user.displayName || userEmail.split('@')[0];

    const sendHeartbeat = async () => {
      try {
        await setDoc(presenceRef, {
          projectId,
          uid: user.uid,
          email: userEmail,
          name: userName,
          initials: getInitials(userName),
          color: getHashColor(user.uid),
          lastSeen: Date.now()
        }, { merge: true });
      } catch (err) {
        console.warn('[Presence] Heartbeat error:', err);
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Heartbeat interval every 15 seconds
    const interval = setInterval(sendHeartbeat, 15000);

    // Clean up on window unload / unmount
    const handleUnload = () => {
      deleteDoc(presenceRef).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);

    // Real-time listener for active users in this project
    const q = query(
      collection(db, 'projectPresence'),
      where('projectId', '==', projectId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const now = Date.now();
      const users: PresenceUser[] = [];

      snapshot.forEach(docSnap => {
        const data = docSnap.data() as PresenceUser;
        // User is considered active if lastSeen is within the last 45 seconds
        if (data.lastSeen && (now - data.lastSeen) < 45000) {
          users.push(data);
        }
      });

      // Sort current user first, then by name
      users.sort((a, b) => {
        if (a.uid === user.uid) return -1;
        if (b.uid === user.uid) return 1;
        return a.name.localeCompare(b.name);
      });

      setActiveUsers(users);
    }, err => {
      console.warn('[Presence] Snapshot error:', err);
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
      unsubscribe();
      deleteDoc(presenceRef).catch(() => {});
    };
  }, [projectId, user]);

  return activeUsers;
}
